import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CountUp from 'react-countup';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Send, Users, CheckCircle, XCircle, Activity, Calendar, Clock, BarChart3, ArrowUpRight, MailOpen, Mail, ArrowLeft } from 'lucide-react';
import { format, subDays, isAfter, isSameMonth } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthState } from "react-firebase-hooks/auth";
import auth from '../../../../firebase.init';
import { Link } from 'react-router-dom';


const EmailAnalyticsDashboard = ({ onLogClick }) => {
  const [user] = useAuthState(auth);
  const [data, setData] = useState({
    allLogs: [],
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  
  const [chartRange, setChartRange] = useState(7);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!user?.email) return;
      try {
        const [userTemplatesRes, usersRes] = await Promise.all([
          axios.get(`https://demo-email-marketing-1-f6e91377e127.herokuapp.com/user-email-templates?ownerEmail=${user.email}`).catch(() => ({ data: [] })),
          axios.get(`https://demo-email-marketing-1-f6e91377e127.herokuapp.com/users?ownerEmail=${user.email}`).catch(() => ({ data: [] }))
        ]);

        const manualUsers = Array.isArray(userTemplatesRes.data) ? userTemplatesRes.data : [];
        const registeredUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
        
        const allUsers = [...manualUsers, ...registeredUsers];
        
        let logs = [];
        allUsers.forEach(user => {
          if (user.messageLogs && Array.isArray(user.messageLogs)) {
            logs = [...logs, ...user.messageLogs];
          }
        });

        logs.sort((a, b) => new Date(b.date) - new Date(a.date));

        setData({
          allLogs: logs,
          totalUsers: allUsers.length
        });
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchAnalyticsData();
      const interval = setInterval(fetchAnalyticsData, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const availableCategories = useMemo(() => {
    const categories = new Set();
    data.allLogs.forEach(log => {
      if (log.category) {
        categories.add(log.category);
      }
    });
    return Array.from(categories);
  }, [data.allLogs]);

  const stats = useMemo(() => {
    const now = new Date();
    const rangeAgo = subDays(now, chartRange);

    const sentThisWeek = data.allLogs.filter(log => log.date && isAfter(new Date(log.date), subDays(now, 7))).length;
    const sentThisMonth = data.allLogs.filter(log => log.date && isSameMonth(new Date(log.date), now)).length;

    const chartDataMap = {};
    for (let i = chartRange - 1; i >= 0; i--) {
      const d = subDays(now, i);
      chartDataMap[format(d, 'MMM dd')] = 0;
    }
    
    data.allLogs.forEach(log => {
      if (log.date) {
        const logDate = new Date(log.date);
        if (isAfter(logDate, rangeAgo)) {
          const key = format(logDate, 'MMM dd');
          if (chartDataMap[key] !== undefined) {
            chartDataMap[key]++;
          }
        }
      }
    });

    const chartData = Object.keys(chartDataMap).map(key => ({
      name: key,
      emails: chartDataMap[key]
    }));

    // Filter recent activity
    const filteredActivity = data.allLogs.filter(log => {
      const matchesSearch = log.subject?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            log.recipientEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            log.templateName?.toLowerCase().includes(searchQuery.toLowerCase());
                            
      const matchesCategory = categoryFilter === 'all' || log.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });

    return {
      totalSent: data.allLogs.length,
      sentThisWeek,
      sentThisMonth,
      totalUsers: data.totalUsers,
      delivered: data.allLogs.length,
      failed: 0,
      chartData,
      recentActivity: filteredActivity.slice(0, 15)
    };
  }, [data, chartRange, searchQuery, categoryFilter]);

  const handleLogClick = (log) => {
    if (onLogClick && typeof onLogClick === 'function') {
      onLogClick(log);
    } else {
      setSelectedLogDetail(log);
    }
  };

  const StatCard = ({ title, value, icon: Icon, colorClass, gradient, delay }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className="premium-card h-100 overflow-hidden" 
      whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(0,0,0,0.4)' }}
    >
      <div className="card-body p-4 position-relative z-1 ">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className={`icon-glow-box ${gradient}`}>
            <Icon size={22} className="text-white" strokeWidth={2.5} />
          </div>
          <ArrowUpRight size={18} className="text-muted opacity-50" />
        </div>
        <div>
          <h3 className="fw-bolder mb-1 text-white display-6" style={{ letterSpacing: '-1px' }}>
            <CountUp end={value} duration={2.5} separator="," preserveValue={true} />
          </h3>
          <p className="text-gray-400 small text-uppercase fw-bold mb-0 tracking-wider">{title}</p>
        </div>
      </div>
      <div className={`card-glow-effect ${gradient}`} />
    </motion.div>
  );

  if (loading && data.allLogs.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5 mb-5" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-info" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading Analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="mb-5 mt-3 container-fluid"
    >
      <Link 
        to="/" 
        className="btn btn-outline-light rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 mb-4"
        style={{ fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)' }}
      >
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>
      
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h4 className="fw-bold text-white mb-1 d-flex align-items-center" style={{ letterSpacing: '-0.5px' }}>
            <BarChart3 className="me-3 text-info" size={28} strokeWidth={2.5} />
            Campaign Analytics
          </h4>
          <p className="text-gray-400 mb-0 ms-5" style={{ fontSize: '0.9rem' }}>Real-time performance metrics and insights</p>
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard title="Total Sent" value={stats.totalSent} icon={Send} gradient="grad-blue" delay={0.1} />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard title="Sent This Week" value={stats.sentThisWeek} icon={Calendar} gradient="grad-cyan" delay={0.2} />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard title="Delivered" value={stats.delivered} icon={CheckCircle} gradient="grad-emerald" delay={0.3} />
        </div>
      
      </div>

      <div className="row g-4">
        {/* Chart Section */}
        <motion.div 
          className="col-12 col-xl-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="premium-card h-100">
            <div className="p-4 pb-0 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <h6 className="fw-bold mb-0 text-white tracking-wider text-uppercase small">Sending Volume</h6>
                <select 
                  className="form-select form-select-sm bg-dark text-white border-gray-800 shadow-none" 
                  style={{ width: 'auto', fontSize: '0.8rem' }}
                  value={chartRange}
                  onChange={(e) => setChartRange(Number(e.target.value))}
                >
                  <option value={7}>Last 7 Days</option>
                  <option value={14}>Last 14 Days</option>
                  <option value={30}>Last 30 Days</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="status-dot pulsing bg-info"></span>
                <span className="text-gray-400 small">Live</span>
              </div>
            </div>
            <div className="p-4" style={{ height: '360px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.chartData} margin={{ top: 20, right: 10, left: -25, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorEmails" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.6}/>
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={15} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', color: '#fff' }}
                    itemStyle={{ color: '#38bdf8', fontWeight: 'bold' }}
                    cursor={{ stroke: 'rgba(14, 165, 233, 0.2)', strokeWidth: 2 }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="emails" 
                    stroke="#0ea5e9" 
                    strokeWidth={4} 
                    fillOpacity={1} 
                    fill="url(#colorEmails)" 
                    activeDot={{ r: 6, strokeWidth: 3, stroke: '#1e293b', fill: '#0ea5e9', className: 'recharts-active-dot' }} 
                    animationDuration={1500} 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Recent Activity Section */}
        <motion.div 
          className="col-12 col-xl-4"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="premium-card h-100 d-flex flex-column">
            <div className="p-4 pb-3 border-bottom border-gray-800">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0 text-white tracking-wider text-uppercase small">Recent Activity</h6>
                <span className="badge bg-primary bg-opacity-25 text-primary rounded-pill px-3 py-1">Latest</span>
              </div>
              <div className="d-flex gap-2">
                <input 
                  type="text" 
                  className="form-control form-control-sm bg-dark text-white border-gray-800 shadow-none"
                  placeholder="Search emails..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select 
                  className="form-select form-select-sm bg-dark text-white border-gray-800 shadow-none w-auto"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {availableCategories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'forSubscriber' ? 'Subscribers' : cat === 'forRegister' ? 'Registered' : cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-0 flex-grow-1" style={{ maxHeight: '350px', overflowY: 'auto' }}>
              {stats.recentActivity.length === 0 ? (
                <div className="text-center py-5">
                  <Clock size={40} className="mb-3 opacity-25 text-white mx-auto d-block" />
                  <p className="mb-0 text-gray-400">No recent activity found.</p>
                </div>
              ) : (
                <div className="list-group list-group-flush bg-transparent">
                  {stats.recentActivity.map((log, idx) => (
                    <motion.button
                      key={idx}
                      whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', x: 4 }}
                      className="list-group-item border-0 border-bottom border-gray-800 px-4 py-3 d-flex align-items-center bg-transparent text-start"
                      onClick={() => handleLogClick(log)}
                      style={{ transition: 'all 0.2s ease' }}
                    >
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '42px', height: '42px', background: 'rgba(56, 189, 248, 0.1)' }}>
                        <Send size={16} className="text-info" />
                      </div>
                      <div className="overflow-hidden flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-semibold text-white text-truncate d-block" style={{ fontSize: '0.9rem', maxWidth: '70%' }}>
                            {log.subject || log.templateName}
                          </span>
                          <small className="text-gray-400" style={{ fontSize: '0.75rem' }}>
                            {log.date ? format(new Date(log.date), 'MMM dd') : ''}
                          </small>
                        </div>
                        <div className="text-gray-500 text-truncate" style={{ fontSize: '0.8rem' }}>
                          To: <span className="text-info opacity-75">{log.recipientEmail}</span>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Email Detail Modal */}
      <AnimatePresence>
        {selectedLogDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1060 }}
            onClick={() => setSelectedLogDetail(null)}
          >
            <motion.div
              initial={{ y: 50, scale: 0.95 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 20, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content" style={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", background: "#111827" }}>
                {/* Gradient Header */}
                <div
                  className="modal-header border-0"
                  style={{
                    background: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
                    color: "white",
                    padding: "24px 30px",
                  }}
                >
                  <div>
                    <h5 className="modal-title mb-1 d-flex align-items-center text-white" style={{ fontSize: "1.25rem" }}>
                      <MailOpen size={22} className="me-2" />
                      Email Details
                    </h5>
                    <p className="mb-0" style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                      {selectedLogDetail.templateName} • {formatDate(selectedLogDetail.date)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedLogDetail(null)}
                  ></button>
                </div>

                <div className="modal-body p-0">
                  {/* Info Cards Row */}
                  <div className="p-4" style={{ background: "rgba(255,255,255,0.02)" }}>
                    <div className="row g-3">
                      {/* Recipient Card */}
                      <div className="col-md-4">
                        <div className="d-flex align-items-center p-3 rounded-3 shadow-sm h-100" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.05)" }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: "44px", height: "44px", background: "linear-gradient(135deg, #667eea, #764ba2)", flexShrink: 0 }}>
                            <Mail size={20} color="white" />
                          </div>
                          <div style={{ overflow: "hidden" }}>
                            <div className="text-gray-400" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Recipient</div>
                            <div className="fw-semibold text-truncate text-white" style={{ fontSize: "0.9rem" }}>
                              {selectedLogDetail.recipientName || "—"}
                            </div>
                            <div className="text-gray-400 text-truncate" style={{ fontSize: "0.8rem" }}>
                              {selectedLogDetail.recipientEmail || "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Send Info Card */}
                      <div className="col-md-4">
                        <div className="d-flex align-items-center p-3 rounded-3 shadow-sm h-100" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.05)" }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: "44px", height: "44px", background: "linear-gradient(135deg, #f093fb, #f5576c)", flexShrink: 0 }}>
                            <Send size={20} color="white" />
                          </div>
                          <div>
                            <div className="text-gray-400" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Send Info</div>
                            <div className="fw-semibold text-white" style={{ fontSize: "0.9rem" }}>Day {selectedLogDetail.day}</div>
                            <div>
                              <span className={`badge ${selectedLogDetail.sendType === 'manual' ? 'bg-warning text-dark' : 'bg-info'}`} style={{ fontSize: "0.7rem" }}>
                                {selectedLogDetail.sendType === 'manual' ? '✋ Manual Send' : '⏰ Scheduled'}
                              </span>
                              {" "}
                              <span className={`badge ${selectedLogDetail.mode === 'ai' ? 'bg-purple' : 'bg-secondary'}`} style={{ fontSize: "0.7rem", background: selectedLogDetail.mode === 'ai' ? '#7c3aed' : undefined }}>
                                {selectedLogDetail.mode === 'ai' ? '🤖 AI Generated' : '📝 HTML'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Category & Date Card */}
                      <div className="col-md-4">
                        <div className="d-flex align-items-center p-3 rounded-3 shadow-sm h-100" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.05)" }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: "44px", height: "44px", background: "linear-gradient(135deg, #4facfe, #00f2fe)", flexShrink: 0 }}>
                            <Clock size={20} color="white" />
                          </div>
                          <div>
                            <div className="text-gray-400" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Sent At</div>
                            <div className="fw-semibold text-white" style={{ fontSize: "0.9rem" }}>{formatDate(selectedLogDetail.date)}</div>
                            {selectedLogDetail.category && (
                              <span className={`badge ${selectedLogDetail.category === 'forSubscriber' ? 'bg-info' : selectedLogDetail.category === 'forRegister' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: "0.7rem" }}>
                                {selectedLogDetail.category === 'forSubscriber' ? 'Subscriber' : selectedLogDetail.category === 'forRegister' ? 'Register' : selectedLogDetail.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject Line */}
                  <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                    <div className="text-gray-400" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Subject</div>
                    <div className="fw-bold text-white" style={{ fontSize: "1.1rem" }}>
                      {selectedLogDetail.subject || "(No subject saved)"}
                    </div>
                  </div>

                  {/* Email Body */}
                  <div className="p-4">
                    <div className="text-gray-400 mb-2" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Email Body</div>
                    {selectedLogDetail.body ? (
                      <div
                        style={{
                          background: "white",
                          color: "black",
                          border: "1px solid #e5e7eb",
                          borderRadius: "12px",
                          padding: "24px",
                          maxHeight: "500px",
                          overflowY: "auto",
                          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.04)",
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedLogDetail.body }}
                      />
                    ) : (
                      <div className="text-center py-5">
                        <MailOpen size={40} className="text-gray-500 mb-2" />
                        <p className="text-gray-500">Email body not available for this log entry.<br/>
                          <small>Older logs may not have saved the full email content.</small>
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer border-0" style={{ padding: "16px 30px", borderTop: "1px solid rgba(255,255,255,0.05) !important" }}>
                  <button
                    className="btn btn-outline-light"
                    onClick={() => setSelectedLogDetail(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx="true">{`
        /* Premium Dark Theme Styles */
        .premium-card {
          background: #111827;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 1.25rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          position: relative;
          transition: all 0.3s ease;
          overflow: hidden;
        }
        
        .premium-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%);
          pointer-events: none;
          z-index: 0;
        }

        .icon-glow-box {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 16px -4px rgba(0,0,0,0.3);
        }

        .card-glow-effect {
          position: absolute;
          width: 150px;
          height: 150px;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.15;
          top: -50px;
          right: -50px;
          z-index: 0;
          pointer-events: none;
        }

        /* Gradients */
        .grad-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .grad-cyan { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
        .grad-emerald { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .grad-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }

        /* Typography & Colors */
        .text-gray-400 { color: #9ca3af; }
        .text-gray-500 { color: #6b7280; }
        .border-gray-800 { border-color: rgba(255,255,255,0.05) !important; }
        .tracking-wider { letter-spacing: 0.05em; }
        
        /* Status Dot */
        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }
        .pulsing {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse-ring {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        /* Custom Scrollbar */
        .flex-grow-1::-webkit-scrollbar { width: 4px; }
        .flex-grow-1::-webkit-scrollbar-track { background: transparent; }
        .flex-grow-1::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1); 
          border-radius: 10px;
        }
        .flex-grow-1::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.2); }
        
        .recharts-active-dot {
          filter: drop-shadow(0px 0px 6px rgba(14, 165, 233, 0.8));
        }
      `}</style>
    </motion.div>
  );
};

export default EmailAnalyticsDashboard;
